<template>
  <div class="choice-list">
    <span v-if="loading" class="mx-auto my-3"><i class="fa fa-circle-o-notch fa-spin"></i></span>
    <el-checkbox-group
      v-else
      :disabled="readOnly || groupReadOnly"
      :value="selectedLabels"
      :name="name"
      :class="{ invalid: isInvalid }"
    >
      <el-checkbox-button
        v-for="entity in options"
        :key="entity.key"
        :id="`choice-item-${entity.key}`"
        :class="{ 'is-checked': isChecked(entity.key) }"
        :label="$i18n.t(entity.value)"
        @change="onChange(entity.key)"
      >
      </el-checkbox-button>
    </el-checkbox-group>
  </div>
</template>
<script>
import EventBus from '../../../utils/event-bus'
import addCoreParams from '../../../utils/add-doto-params'
import { includes, xor, isEmpty, cloneDeep, get, isNull, isUndefined } from 'lodash'

/**
 * @class ChoiceList
 *
 * @description This component renders a list of checkbox or radiobutton inputs
 */
export default {
  name: 'choice-list',
  inheritAttrs: false,
  data() {
    return {
      options: [],
      loading: false,
    }
  },
  props: {
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    type: {
      type: String,
    },
    list: {
      type: [Array, Object, String],
      default: () => [],
    },
    defaultValue: {
      type: [Array, Number, String],
    },
    value: {
      type: [Array, Number, String],
    },
    isInvalid: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    groupReadOnly: {
      type: Boolean,
      default: false,
    },
    componentId: {
      type: String,
      default: '',
    },
    localizeData: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    castValueToArray() {
      if (!Array.isArray(this.value)) {
        if (isNull(this.value) || isUndefined(this.value)) {
          this.emitChange({ value: [] })
        } else {
          this.emitChange({ value: [ this.value ] })
        }
      }
    },
    emitChange({ value, trigger = '' }) {
      this.$emit('change', { name: this.name, value, trigger })
      this.$emit('form-item-changed', { name: this.name, value, trigger })
    },
    isChecked(id) {
      const value = Array.isArray(this.value) ? this.value.map((val) => String(val)) : String(this.value)
      if (this.type === 'checkboxlist' || this.type === 'radiobuttonlist') {
        return value.includes(String(id))
      }
      return String(this.value) === String(id)
    },
    onChange(key) {
      let value = []
      if (Array.isArray(this.value)) {
        value = [...this.value]
      } else if (this.value) {
        value = [this.value]
      }

      if (this.type === 'checkboxlist') {
        value = xor(value, [key])
      } else {
        value = value.includes(key) ? [] : [ key ]
      }

      this.emitChange({ value, trigger: 'itemChange' })
    },
    labelClass(id) {
      return `control-label ${this.isChecked(id) ? 'checked' : ''}`
    },
    getOptions() {
      if (typeof this.list === 'string' && this.list.length) {
        this.loading = true
        fetch(addCoreParams(options, this.$CORE.params))
          .then((response) => response.json())
          .then((options) => {
            this.options = this.convertList(options)
          })
          .finally(() => {
            this.setDefaultValue()
            this.loading = false
          })
          .catch((error) => console.error(error.message))
      } else {
        this.options = this.convertList(get(this.list, 'options', []))
        this.setDefaultValue()
      }
      this.checkOptions()
    },
    convertList(options) {
      if (!Array.isArray(options) && options.length !== 0) {
        return Object.entries(options).reduce((acc, entry) => {
          acc.push({
            key: entry[0],
            value: entry[1],
          })
          return acc
        }, [])
      }
      if (this.localizeData) {
        options.map((entry) => {
          entry.value = this.$i18n.t(entry.value)
        })
      }
      return options
    },
    setDefaultValue() {
      if (!this.value && this.defaultValue) {
        this.emitChange({ value: this.defaultValue })
      }
    },
    onClear() {
      this.emitChange({ value: [] })
    },
    checkOptions() {
      if (!isEmpty(this.missingOptions)) {
        this.$notify({
          title: this.$i18n.t('id_missing_from_table'),
          message: this.$i18n.interpolate(this.$i18n.t('evaluated_value_cannot_be_found: ${missing}'), {
            missing: this.missingOptions.join(','),
          }),
          type: 'warning',
          duration: 5000,
        })
      }
    },
  },
  computed: {
    selectedLabels() {
      let selectedLabelArray = []
      if (Array.isArray(this.options)) {
        this.options.forEach((option) => {
          if (includes(this.value, option.key)) {
            selectedLabelArray.push(option.value)
          }
        })
      }
      return selectedLabelArray
    },
    fieldType() {
      return this.type === 'checkboxlist' ? 'checkbox' : 'radio'
    },
    fieldName() {
      return this.type === 'checkboxlist' ? `${this.name}[]` : this.name
    },
    missingOptions() {
      const missing = []
      if (Array.isArray(this.value) && !isEmpty(this.value)) {
        this.value.forEach((value, idx) => {
          if (value && isEmpty(this.options.filter((option) => String(option.key) === String(value)))) {
            let cloneValue = cloneDeep(this.value)
            cloneValue = cloneValue.splice(idx, 1)
            missing.push(value)
            this.emitChange({ value: cloneValue, trigger: 'itemChange' })
          }
        })
      } else {
        const selectedOption = this.options.filter((option) => String(option.key) === String(this.value))
        if (!selectedOption) missing.push(selectedOption)
      }
      return missing
    },
  },
  mounted() {
    this.getOptions()
    EventBus.$on('selection-list', (evt) => {
      if (this.$storeNamespace && this.$get('newElementKey') === this.componentId) {
        this.isChecked(String(get(evt, 'BODY.idMaintable')))
        this.onChange(get(evt, 'BODY.idMaintable'))
      }
    })
    this.castValueToArray()
    EventBus.$on('search-clear', this.onClear)
  },
  beforeDestroy() {
    EventBus.$off('selection-list')
    EventBus.$off('search-clear')
  },
}
</script>

<style scoped lang="scss">
form .row .checkbox,
form .row .radio {
  margin-bottom: 15px;
}
//@todo jobb disable icon
[disabled] {
  cursor:
    url('../../../assets/img/notallowed24.png') 2 2,
    not-allowed !important;
}
[disabled] label {
  cursor:
    url('../../../assets/img/notallowed24.png') 2 2,
    not-allowed !important;
  opacity: 0.65;
}
</style>
